// ============================================================
// TL4K Socket.IO — JWT Auth Helper
// ============================================================
// Utilities for authenticating Socket.IO connections with JWT.
// Used by the centralized SocketService and by apps that need
// to pass auth tokens during handshake.
// ============================================================

import type { SocketAuthConfig } from './types';

/**
 * Get the current user's JWT token for Socket.IO auth.
 *
 * Checks multiple storage locations in order:
 *   1. `tl4k_access_token` (standard TL4K key)
 *   2. `auth_token` (fallback)
 *
 * @returns JWT token string or undefined if not found
 */
export function getAuthToken(): string | undefined {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return undefined;
  }

  try {
    return localStorage.getItem('tl4k_access_token')
      ?? localStorage.getItem('auth_token')
      ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build the Socket.IO handshake auth configuration.
 *
 * @param options - Optional overrides for token and additional data
 * @returns Auth config object for Socket.IO `auth` option
 *
 * @example
 * const socket = io(url, {
 *   ...getDefaultSocketOptions(),
 *   auth: buildSocketAuth({ token: getAuthToken() }),
 * });
 */
export function buildSocketAuth(options?: SocketAuthConfig): Record<string, unknown> {
  const token = options?.token ?? getAuthToken();
  const result: Record<string, unknown> = {};

  if (token) {
    result.token = token;
  }

  if (options?.data) {
    Object.assign(result, options.data);
  }

  return result;
}

/**
 * Check if Socket.IO auth is required by the server.
 *
 * Reads `SOCKET_IO_REQUIRE_AUTH` from env (server-side flag).
 * On the client side, this is determined by whether we have a token.
 *
 * @returns true if we have a token available
 */
export function hasSocketAuth(): boolean {
  return getAuthToken() !== undefined;
}
